import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { ConfirmDialogService } from '../../confirm-dialog/confirm-dialog.service';
import { VariantBarcode, VariantBarcodeService } from '../variant-barcode.service';

/**
 * Barcodes of one saved variant: list, add, make primary, retire, generate.
 *
 * Barcodes are IDENTITY, independent of pricing — this panel never touches a
 * price row. It only works for a variant that already exists (has an id);
 * for a brand-new product the EAN can still be typed on the pricing row or
 * tagged afterwards on the Bulk EAN grid.
 */
@Component({
  selector: 'app-variant-barcodes-panel',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './variant-barcodes-panel.component.html',
})
export class VariantBarcodesPanelComponent implements OnChanges {
  @Input({ required: true }) variantId!: string | null;
  @Input() sku: string | null = null;

  private readonly service = inject(VariantBarcodeService);
  private readonly toastr = inject(ToastrService);
  private readonly confirmDialog = inject(ConfirmDialogService);

  barcodes: VariantBarcode[] = [];
  loading = false;
  busy = false;
  newCode = '';
  showRetired = false;

  get active(): VariantBarcode[] {
    return this.barcodes.filter((b) => b.isActive);
  }

  get retired(): VariantBarcode[] {
    return this.barcodes.filter((b) => !b.isActive);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['variantId']) this.reload();
  }

  reload(): void {
    if (!this.variantId) {
      this.barcodes = [];
      return;
    }
    this.loading = true;
    this.service.list(this.variantId).subscribe({
      next: (rows) => {
        this.barcodes = rows;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.toastr.error('Could not load barcodes.');
      },
    });
  }

  add(): void {
    const code = this.newCode.trim();
    if (!this.variantId || !code || this.busy) return;
    this.busy = true;
    this.service.add({ variantId: this.variantId, code }).subscribe({
      next: (res) => {
        this.busy = false;
        this.newCode = '';
        if (res.outcome === 'unchanged') this.toastr.info(`${code} is already on this SKU.`);
        else this.toastr.success(`${code} ${res.outcome === 'reactivated' ? 'reactivated' : 'tagged'}.`);
        if (res.warning) this.toastr.warning(res.warning, undefined, { timeOut: 8000 });
        this.reload();
      },
      error: (err) => {
        this.busy = false;
        this.toastr.error(this.messageOf(err, 'Could not add the barcode.'));
      },
    });
  }

  /** Inline edit: which row is being edited and the code typed so far. */
  editingId: string | null = null;
  editCode = '';

  startEdit(row: VariantBarcode): void {
    this.editingId = row.id;
    this.editCode = row.code;
  }

  cancelEdit(): void {
    this.editingId = null;
    this.editCode = '';
  }

  /** Replace the code in one step: new code tagged (keeps primary), old one retired. */
  saveEdit(row: VariantBarcode): void {
    const code = this.editCode.trim();
    if (!code || this.busy) return;
    if (code === row.code) {
      this.cancelEdit();
      return;
    }
    this.busy = true;
    this.service.replace(row.id, code).subscribe({
      next: (res) => {
        this.busy = false;
        this.cancelEdit();
        this.toastr.success(`${row.code} replaced with ${code}.`);
        if (res.warning) this.toastr.warning(res.warning, undefined, { timeOut: 8000 });
        this.reload();
      },
      error: (err) => {
        this.busy = false;
        this.toastr.error(this.messageOf(err, 'Could not replace the barcode.'));
      },
    });
  }

  setPrimary(row: VariantBarcode): void {
    if (row.isPrimary || this.busy) return;
    this.busy = true;
    this.service.setPrimary(row.id).subscribe({
      next: () => {
        this.busy = false;
        this.reload();
      },
      error: (err) => {
        this.busy = false;
        this.toastr.error(this.messageOf(err, 'Could not change the primary barcode.'));
      },
    });
  }

  async retire(row: VariantBarcode): Promise<void> {
    if (this.busy) return;
    const ok = await this.confirmDialog.confirm({
      title: 'Retire barcode?',
      message: `Remove <strong>${row.code}</strong> from ${this.sku ?? 'this SKU'}? Scans of this code will no longer find this SKU, and the code becomes free to tag on another SKU. It stays visible under "retired" and can be added again later.`,
      confirmText: 'Retire',
      cancelText: 'Keep',
    });
    if (!ok) return;
    this.busy = true;
    this.service.deactivate(row.id).subscribe({
      next: () => {
        this.busy = false;
        this.toastr.success(`${row.code} retired.`);
        this.reload();
      },
      error: (err) => {
        this.busy = false;
        this.toastr.error(this.messageOf(err, 'Could not retire the barcode.'));
      },
    });
  }

  private messageOf(err: any, fallback: string): string {
    const m = err?.error?.message;
    return typeof m === 'string' ? m : Array.isArray(m) ? m.join('; ') : fallback;
  }

  trackById(_: number, b: VariantBarcode): string {
    return b.id;
  }
}
